import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Bookmark16Filled,
    Bookmark16Regular,
    ChevronDown12Regular,
    Mic20Regular,
    MicOff20Regular,
    Send16Regular,
} from '@fluentui/react-icons';
import useSpeechDictation, {
    formatSpeechDictationError,
} from '../../hooks/useSpeechDictation';
import { contextModeLabel, titleFromPromptText } from '../../utils/askAiChatMeta';
import {
    formatInsightsOriginIndicator,
    isInsightsModuleAskOrigin,
} from '../../utils/intelligenceInsightsAskLensOrigin';
import {
    createJournalAnalysisPrompt,
    fetchJournalAnalysisPrompts,
} from '../../redux/journalAnalysisSlice';
import RecordingPopover from '../Journal/Analysis/RecordingPopover';
import styles from './askAi.module.css';

/**
 * @param {'empty'|'thread'} variant
 * empty = Figma empty-state stacked composer
 * thread = Journal-style horizontal composer (bookmark | input | mic | send)
 */
const AskAiComposer = ({
    question,
    onQuestionChange,
    contextMode,
    onOpenChangeContext,
    onOpenSavedPrompts,
    onSubmit,
    submitting = false,
    placeholder,
    showHint = true,
    compact = false,
    variant = 'empty',
    sourcesReady = null,
    sourcesProcessing = null,
    insightsOrigin = null,
}) => {
    const moduleAsk = isInsightsModuleAskOrigin(insightsOrigin);
    const moduleContextLabel =
        insightsOrigin?.indicatorLabel || formatInsightsOriginIndicator(insightsOrigin);
    const dispatch = useDispatch();
    const { savedPrompts } = useSelector(state => state.journalAnalysis || {});
    const committedDraftRef = useRef(question || '');
    const textareaRef = useRef(null);
    const isListeningRef = useRef(false);

    useEffect(() => {
        // While dictating, question includes interim text — don't clobber the committed base.
        if (!isListeningRef.current) {
            committedDraftRef.current = question || '';
        }
    }, [question]);

    const canSubmit = Boolean(question?.trim()) && !submitting;

    const appendDictationText = useCallback((base, text) => {
        const normalized = (text || '').trim();
        if (!normalized) return base || '';
        const needsSpace = (base || '').trim().length > 0 && !/\s$/.test(base || '');
        return `${base || ''}${needsSpace ? ' ' : ''}${normalized}`;
    }, []);

    const handleDictationFinal = useCallback(
        text => {
            const next = appendDictationText(committedDraftRef.current, text);
            committedDraftRef.current = next;
            onQuestionChange(next);
        },
        [appendDictationText, onQuestionChange],
    );

    const handleDictationInterim = useCallback(
        text => {
            onQuestionChange(appendDictationText(committedDraftRef.current, text));
        },
        [appendDictationText, onQuestionChange],
    );

    const { isListening, start, stop } = useSpeechDictation({
        onFinalTranscript: handleDictationFinal,
        onInterimChange: handleDictationInterim,
        onError: msg => toast.error(msg),
    });

    useEffect(() => {
        isListeningRef.current = isListening;
    }, [isListening]);

    const handleToggleMic = async () => {
        if (isListening) {
            await stop();
            // Drop any leftover interim text; keep committed finals only.
            onQuestionChange(committedDraftRef.current || '');
            return;
        }
        committedDraftRef.current = question || '';
        try {
            await start();
        } catch (e) {
            toast.error(formatSpeechDictationError(e?.message || 'Could not start voice dictation.'));
        }
    };

    const handleKeyDown = e => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (canSubmit) onSubmit();
        }
    };

    const openContext = () => onOpenChangeContext?.();

    const isDraftSavedPrompt = Boolean(
        (savedPrompts || []).some(
            p => String(p?.prompt || '').trim() === String(question || '').trim(),
        ),
    );

    const handleSavePrompt = async () => {
        const text = String(question || '').trim();
        if (!text) return toast.error('Enter a prompt to save.');
        if (isDraftSavedPrompt) return;
        try {
            await dispatch(
                createJournalAnalysisPrompt({
                    title: titleFromPromptText(text),
                    prompt: text,
                }),
            ).unwrap();
            await dispatch(fetchJournalAnalysisPrompts());
            toast.success('Prompt saved');
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Could not save prompt');
        }
    };

    const autoGrow = () => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    };

    useEffect(() => {
        if (variant === 'thread') autoGrow();
    }, [question, variant]);

    /* —— Thread / Journal-style horizontal composer —— */
    if (variant === 'thread') {
        const readyCount = sourcesReady != null ? Number(sourcesReady) : null;
        const processingCount =
            sourcesProcessing != null ? Number(sourcesProcessing) : null;

        return (
            <div className={styles.threadComposerInner}>
                <div className={styles.composerContext}>
                    <span>
                        {moduleAsk ? (
                            moduleContextLabel
                        ) : readyCount != null ? (
                            <>
                                Using {readyCount} source{readyCount === 1 ? '' : 's'}
                                {processingCount > 0 ? ` · ${processingCount} processing` : ''}
                            </>
                        ) : (
                            <>Context: {contextModeLabel(contextMode)}</>
                        )}
                    </span>
                    {!moduleAsk ? (
                        <button
                            type="button"
                            className={styles.composerChangeLink}
                            onClick={openContext}>
                            Change
                        </button>
                    ) : null}
                </div>

                <div className={styles.journalComposer}>
                    <button
                        type="button"
                        className={`${styles.iconAction} ${
                            isDraftSavedPrompt ? styles.iconActionSaved : ''
                        }`}
                        title={isDraftSavedPrompt ? 'Prompt saved' : 'Save prompt'}
                        onClick={() => void handleSavePrompt()}>
                        {isDraftSavedPrompt ? (
                            <Bookmark16Filled color="var(--Brand-Secondary, #8e2de2)" />
                        ) : (
                            <Bookmark16Regular />
                        )}
                    </button>
                    <textarea
                        ref={textareaRef}
                        value={question}
                        rows={1}
                        onChange={e => {
                            const value = e.target.value;
                            onQuestionChange(value);
                            if (!isListening) committedDraftRef.current = value;
                        }}
                        placeholder={placeholder || 'Ask a follow-up question…'}
                        onKeyDown={handleKeyDown}
                        disabled={submitting}
                    />
                    <div className={styles.composerRight}>
                        <div className={styles.micWrap}>
                            {isListening ? (
                                <RecordingPopover active={isListening} onStop={handleToggleMic} />
                            ) : null}
                            <button
                                type="button"
                                className={`${styles.iconAction} ${
                                    isListening ? styles.micActive : ''
                                }`}
                                title={isListening ? 'Stop dictation' : 'Start voice dictation'}
                                aria-label={
                                    isListening ? 'Stop dictation' : 'Start voice dictation'
                                }
                                aria-pressed={isListening}
                                onClick={handleToggleMic}>
                                {isListening ? <MicOff20Regular /> : <Mic20Regular />}
                            </button>
                        </div>
                        <button
                            type="button"
                            className={styles.sendBtn}
                            disabled={!canSubmit}
                            onClick={() => onSubmit()}
                            aria-label="Send">
                            <Send16Regular />
                        </button>
                    </div>
                </div>
                {showHint ? (
                    <p className={styles.composerHint}>
                        Press Enter to send · Shift + Enter for new line
                    </p>
                ) : null}
            </div>
        );
    }

    /* —— Empty-state stacked composer (Figma LI_Ask AI) —— */
    return (
        <div className={`${styles.composerWrap} ${compact ? styles.composerWrapCompact : ''}`}>
            <div className={`${styles.composer} ${compact ? styles.composerCompact : ''}`}>
                <textarea
                    className={styles.composerInput}
                    value={question}
                    onChange={e => onQuestionChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder || 'Ask about your project…'}
                    rows={compact ? 2 : 3}
                    disabled={submitting}
                />
                <div className={styles.composerToolbar}>
                    <div className={styles.composerLeft}>
                        {!moduleAsk ? (
                            <button
                                type="button"
                                className={styles.contextBtn}
                                onClick={openContext}>
                                Context: {contextModeLabel(contextMode)}
                                <ChevronDown12Regular />
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className={styles.savedPromptsLink}
                            onClick={() => onOpenSavedPrompts?.()}>
                            Saved Prompts
                        </button>
                    </div>
                    <button
                        type="button"
                        className={styles.askBtn}
                        disabled={!canSubmit}
                        onClick={() => onSubmit()}>
                        Ask
                    </button>
                </div>
            </div>
            {showHint ? (
                <p className={styles.composerHint}>
                    {moduleAsk
                        ? 'Answers are grounded in this module’s stored analysis only.'
                        : 'Answers cite project sources. Verify critical claims in source material.'}
                </p>
            ) : null}
        </div>
    );
};

export default AskAiComposer;
